import { memo, useMemo, type FC, useState, useCallback } from 'react';
import { navigationHelper } from 'NavigationHelpers';
import { useSuspenseFeatureGate } from 'FrontRoyalStatsig';
import { type TutorBotDataReceivedCallback, VoiceAgentRoom } from 'TutorBotVoiceAgent';
import { useTranslation } from 'react-i18next';
import './locales/TutorBotCoach/CoachPage-en.json';
import 'TutorBotVoiceAgent/locales/TutorBotVoiceAgent/TutorBotVoiceAgent-en.json';
import { useEventLogger, useSuspenseCurrentUser, useTargetBrandConfig } from 'FrontRoyalAngular';
import { UI_CONTEXT_COACH_AUDIO } from 'TutorBotConversation';
import { useSendPing } from 'Status';
import { type CurrentUser } from 'Users';
import { type ProgramApplication } from 'ProgramApplication';
import { isPostApplicationGoalSettingSessionOpportunity } from '../../utils/typeGuards';
import { type CoachSessionOpportunity } from '../../Coach.types';
import { getLaunchableCoachSessionOpportunityForUser } from '../../utils/CoachSessionOpportunity';
import './locales/TutorBotCoach/CoachIntro-en.json';

function useKeepTheMomentum(programApplication: ProgramApplication | null) {
    const targetBrandConfig = useTargetBrandConfig();
    const { t } = useTranslation('back_royal', { keyPrefix: 'TutorBotCoach.CoachPage' });
    const keepTheMomentum = useMemo(() => {
        if (!programApplication) return '';
        const localeKey = programApplication.declineScholarshipConsideration
            ? 'keepMomentumNoScholarship'
            : 'keepMomentumScholarship';
        return t(localeKey, { brandName: targetBrandConfig.brandNameShort });
    }, [programApplication, t, targetBrandConfig.brandNameShort]);

    return keepTheMomentum;
}

export const CoachPagePresentation: FC<{
    goHome: () => void;
    coachSessionOpportunity: CoachSessionOpportunity;
    currentUser: CurrentUser;
}> = ({ goHome, coachSessionOpportunity, currentUser }) => {
    const EventLogger = useEventLogger();
    const { sendPing } = useSendPing();
    const programApplication = useMemo(() => {
        if (isPostApplicationGoalSettingSessionOpportunity(coachSessionOpportunity)) {
            return (
                currentUser.programApplications.find(pa => pa.id === coachSessionOpportunity.programApplicationId) ||
                null
            );
        }

        return null;
    }, [coachSessionOpportunity, currentUser.programApplications]);

    // Using two different locale files here. Putting the more generic stuff into
    // TutorBotVoiceAgent where it could be re-used, and the stuff that is specific
    // to coaching here.
    const { t } = useTranslation('back_royal');
    const { t: tIntro } = useTranslation('back_royal', {
        keyPrefix: 'TutorBotCoach.CoachIntro',
    });
    const brandName = useTargetBrandConfig().brandNameShort;

    const keepTheMomentum = useKeepTheMomentum(programApplication);

    const introMessages = useMemo(
        () => [tIntro('join_a_session', { brandName }), tIntro('it_is_a_chance_to'), tIntro('find_a_quiet_spot')],
        [tIntro, brandName],
    );

    const onDataReceived: TutorBotDataReceivedCallback = useCallback(
        async ({ message, conversationId }) => {
            if (message?.type === 'sessionCompleted') {
                EventLogger.log('agent_reported_coach_session_complete', {
                    tutor_bot_conversation_id: conversationId,
                });

                // FIXME: see https://trello.com/c/7DUMeWh8
                await EventLogger.tryToSaveBuffer!();

                // This will flush the event and trigger a ping, which will load the updated
                // coach session opportunity and allow the user to exit back out of the conversation
                // to the PostDiscussion screen
                sendPing();
            }
        },
        [EventLogger, sendPing],
    );

    // Until the session is complete, the user is allowed to cancel the session and go
    // out to the dashboard and is not allowed to view the PostDiscussion UI. Once the
    // session is complete, the user can only exit the conversation by going to the
    // PostDiscussion UI. This ensures that we don't show the "Do this later" link when
    // the session is already complete.
    let allowPostDiscussion = false;
    let abortFromRoom: (() => void) | null = goHome;
    if (coachSessionOpportunity.complete) {
        allowPostDiscussion = true;
        abortFromRoom = null;
    }

    // Because this page is rendered without the app shell, it needs to be absolute in order
    // to make it stretch the full height of the page.
    return (
        <div className="flex h-screen w-screen items-center justify-center bg-gradient-to-bl from-white to-[#F7F7F7] notch:mt-[calc(-1*env(safe-area-inset-top))] notch:pb-[calc(env(safe-area-inset-bottom)+50px)] notch:pt-[env(safe-area-inset-top)]">
            <div className="relative mx-auto flex h-screen w-full items-center justify-center px-2.5">
                <VoiceAgentRoom
                    data-testid="voice-agent-room"
                    agentIdentifier="coach"
                    extraTokenAttributes={{
                        coachSessionOpportunityId: coachSessionOpportunity.id,
                    }}
                    uiContext={UI_CONTEXT_COACH_AUDIO}
                    color="purple"
                    cardHeader={tIntro('thank_you_for_applying')!}
                    cardTexts={[tIntro('we_have_received_your_submission', { brandName })!]}
                    introMessages={introMessages}
                    introWidth="669"
                    startDiscussionText={t('TutorBotCoach.CoachPage.startCoachingSession')}
                    abortText={t('TutorBotVoiceAgent.TutorBotVoiceAgent.doThisLater')}
                    abortFromRoom={abortFromRoom}
                    allowPostDiscussion={allowPostDiscussion}
                    abortConfirmHeader={t('TutorBotCoach.CoachPage.confirm')}
                    abortConfirmText={t('TutorBotCoach.CoachPage.confirmEndSessionQuestion')}
                    abortConfirmContinueButtonText={t('TutorBotCoach.CoachPage.endSession')}
                    abortConfirmCancelButtonText={t('TutorBotCoach.CoachPage.continueSession')}
                    postDiscussionButtonConfig={{
                        buttonText: t('TutorBotCoach.CoachPage.goToDashboard'),
                        action: goHome,
                    }}
                    postDiscussionCard={{
                        headerText: t('TutorBotCoach.CoachPage.yourCoachingSessionIsComplete'),
                        bodyParagraphs: [keepTheMomentum, t('TutorBotCoach.CoachPage.youCanExplore')],
                    }}
                    onDataReceived={onDataReceived}
                    sessionWillEndSoonMessageKey="wrapThingsUp"
                />
            </div>
        </div>
    );
};

const CoachPageComponent: FC = () => {
    const currentUser = useSuspenseCurrentUser();
    const coachEnabled = useSuspenseFeatureGate('voice_coach');

    // We decide once when the component is first mounted whether there is a valid opportunity. After
    // that we will continue to use it even if it is completed while this component is mounted (which
    // would cause getLaunchableCoachSessionOpportunityForUser to stop returning it)
    const coachSessionOpportunityId = useState(() => {
        const initialOpportunity = getLaunchableCoachSessionOpportunityForUser(currentUser);
        if (!initialOpportunity) return null;
        return initialOpportunity?.id;
    })[0];

    const coachSessionOpportunity = useMemo(
        () => currentUser?.coachSessionOpportunities.find(co => co.id === coachSessionOpportunityId) || null,
        [coachSessionOpportunityId, currentUser?.coachSessionOpportunities],
    );

    const { goHome } = navigationHelper();

    if (!currentUser || !coachEnabled || !coachSessionOpportunity) {
        setTimeout(() => goHome(), 0); //  have to timeout to prevent an error navigating while routing
        return null;
    }

    return (
        <CoachPagePresentation
            goHome={goHome}
            coachSessionOpportunity={coachSessionOpportunity}
            currentUser={currentUser}
        />
    );
};

export const CoachPage = memo(CoachPageComponent) as typeof CoachPageComponent;
